import { TabContext, TabList } from '@mui/lab';
import { Box } from '@mui/material';
import { useEffect, useMemo, useState, SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { IconArrowCircleLeftMS } from '../../../../../constants/icons';
import { AccessKey, canSee } from '../../../../../helpers/roleAccess';
import { useCurrentRole } from '../../../../../hooks/useCurrentRole';
import { NoticeDetailsConfiguration } from './Configuration';
import { NoticeDetailsLicensed } from './Licensed';
import { NoticeDetailsPoleLimits } from './PoleLimits';
import { NoticeDetailsRequest } from './Request';
import {
  BackButton,
  ConfigurationWrapper,
  StyledTab,
  StyledTabPanel,
} from './styles';

type SubTabValue = 'configuration' | 'licensed' | 'request' | 'pole-limits';

const SUB_TABS: {
  value: SubTabValue;
  label: string;
  key: AccessKey;
  Component: () => JSX.Element;
}[] = [
  {
    value: 'configuration',
    label: 'Configurações do edital',
    key: 'noticeConfig',
    Component: NoticeDetailsConfiguration,
  },
  {
    value: 'licensed',
    label: 'Credenciados',
    key: 'noticeLicensed',
    Component: NoticeDetailsLicensed,
  },
  {
    value: 'request',
    label: 'Pedidos de credenciamento',
    key: 'noticeRequests',
    Component: NoticeDetailsRequest,
  },
  {
    value: 'pole-limits',
    label: 'Limites por polo',
    key: 'noticeLimits',
    Component: NoticeDetailsPoleLimits,
  },
];

export function NoticeDetails(): JSX.Element {
  const [selectedTabValue, setSelectedTabValue] = useState<SubTabValue>();
  const navigate = useNavigate();
  const role = useCurrentRole();

  const visibleTabs = useMemo(
    () => SUB_TABS.filter((tab) => canSee(tab.key, role)),
    [role]
  );

  // Garante que a aba ativa seja sempre uma permitida ao papel (default = 1ª).
  useEffect(() => {
    setSelectedTabValue((current) =>
      current && visibleTabs.some((tab) => tab.value === current)
        ? current
        : visibleTabs[0]?.value
    );
  }, [visibleTabs]);

  const handleChangeTab = (
    event: SyntheticEvent,
    newValue: SubTabValue
  ): void => {
    setSelectedTabValue(newValue);
  };

  const navigateBack = (): void => navigate('/the-hive/notices');

  return (
    <>
      <BackButton onClick={navigateBack}>{IconArrowCircleLeftMS}</BackButton>
      <ConfigurationWrapper>
        {selectedTabValue && (
          <TabContext value={selectedTabValue}>
            <Box>
              <TabList onChange={handleChangeTab} variant="scrollable">
                {visibleTabs.map((tab) => (
                  <StyledTab
                    key={tab.value}
                    id={tab.value}
                    value={tab.value}
                    label={tab.label}
                  />
                ))}
              </TabList>
            </Box>
            {visibleTabs.map((tab) => (
              <StyledTabPanel key={tab.value} value={tab.value}>
                <tab.Component />
              </StyledTabPanel>
            ))}
          </TabContext>
        )}
      </ConfigurationWrapper>
    </>
  );
}
